import {
	AssignmentSetupType,
	Step,
	getStepsByType,
	getScheduledEditSteps
} from 'LearningManager/constants/createAssignment.constant';
import React, {
	createContext,
	useContext,
	useEffect,
	useMemo,
	useState
} from 'react';
import { useLocation } from 'react-router-dom';

interface ConfigureState {
	assignmentName: string;
	certDuration: string;
	assignmentStart: string;
	dueDate: string;
	assignmentType: string;
}

interface CustomSetupState {
	selectedLearningPathCourseIds: number[];
	selectedIndividualCourseIds: number[];
	selectedAssignUsersOptionId: number | null;
	selectedOrganizationIds: number[];
	selectedPropertyIds: number[];
	selectedRoleIds: number[];
	selectedRolePropertyIds: number[];
	selectedGroupIds: number[];
	selectedGroupPropertyIds: number[];
	selectedIndividualUserIds: number[];
	selectedIndividualPropertyIds: number[];
}

interface QuickSetupState {
	selectedLearningPathCourseIds: number[];
}

interface AssignmentDraft {
	configure: ConfigureState;
	quickSetup: QuickSetupState;
	customSetup: CustomSetupState;
}

const DEFAULT_DRAFT: AssignmentDraft = {
	configure: {
		assignmentName: '',
		certDuration: '',
		assignmentStart: '',
		dueDate: '',
		assignmentType: ''
	},
	quickSetup: {
		selectedLearningPathCourseIds: []
	},
	customSetup: {
		selectedLearningPathCourseIds: [],
		selectedIndividualCourseIds: [],
		selectedAssignUsersOptionId: null,
		selectedOrganizationIds: [],
		selectedPropertyIds: [],
		selectedRoleIds: [],
		selectedRolePropertyIds: [],
		selectedGroupIds: [],
		selectedGroupPropertyIds: [],
		selectedIndividualUserIds: [],
		selectedIndividualPropertyIds: []
	}
};

const EXISTING_ASSIGNMENT_DRAFTS: Record<string, Partial<AssignmentDraft>> = {
	'1': {
		configure: {
			assignmentName: '[Assignment Name C]',
			certDuration: 'one-year',
			assignmentStart: 'immediate',
			dueDate: 'none',
			assignmentType: 'one-time-assignment'
		},
		quickSetup: {
			selectedLearningPathCourseIds: [101, 201, 202]
		}
	},
	'3': {
		configure: {
			assignmentName: '[Assignment Name C]',
			certDuration: 'custom',
			assignmentStart: 'schedule',
			dueDate: 'custom',
			assignmentType: 'ongoing-assignment'
		},
		customSetup: {
			selectedLearningPathCourseIds: [1, 3],
			selectedIndividualCourseIds: [2, 4],
			selectedAssignUsersOptionId: 634,
			selectedOrganizationIds: [],
			selectedPropertyIds: [],
			selectedRoleIds: [1, 2],
			selectedRolePropertyIds: [1, 3],
			selectedGroupIds: [],
			selectedGroupPropertyIds: [],
			selectedIndividualUserIds: [],
			selectedIndividualPropertyIds: []
		}
	}
};

const buildDraftFromAssignmentId = (assignmentId: string | null) => {
	if (!assignmentId) {
		return DEFAULT_DRAFT;
	}

	const existing = EXISTING_ASSIGNMENT_DRAFTS[assignmentId];
	if (!existing) {
		return DEFAULT_DRAFT;
	}

	return {
		configure: {
			...DEFAULT_DRAFT.configure,
			...(existing.configure ?? {})
		},
		quickSetup: {
			...DEFAULT_DRAFT.quickSetup,
			...(existing.quickSetup ?? {})
		},
		customSetup: {
			...DEFAULT_DRAFT.customSetup,
			...(existing.customSetup ?? {})
		}
	};
};

interface StepperContextValue {
	current: number;
	steps: Step[];
	totalSteps: number;
	isFirst: boolean;
	isLast: boolean;
	setupType: AssignmentSetupType;
	isEditMode: boolean;
	isScheduledEdit: boolean;
	draft: AssignmentDraft;
	setSetupType: React.Dispatch<React.SetStateAction<AssignmentSetupType>>;
	updateDraft: (updater: (prev: AssignmentDraft) => AssignmentDraft) => void;
	updateConfigure: (data: Partial<ConfigureState>) => void;
	toggleQuickCourseSelection: (id: number) => void;
	toggleCustomCourseSelection: (
		field: 'selectedLearningPathCourseIds' | 'selectedIndividualCourseIds',
		id: number
	) => void;
	toggleAssignUsersSelection: (
		field:
			| 'selectedOrganizationIds'
			| 'selectedPropertyIds'
			| 'selectedRoleIds'
			| 'selectedGroupIds'
			| 'selectedIndividualUserIds',
		id: number
	) => void;
	setAssignUsersSelection: (
		field:
			| 'selectedOrganizationIds'
			| 'selectedPropertyIds'
			| 'selectedRoleIds'
			| 'selectedGroupIds'
			| 'selectedIndividualUserIds',
		ids: number[]
	) => void;
	setAssignUsersPropertyFilter: (
		field:
			| 'selectedRolePropertyIds'
			| 'selectedGroupPropertyIds'
			| 'selectedIndividualPropertyIds',
		ids: number[]
	) => void;
	setSelectedAssignUsersOptionId: (id: number) => void;
	next: () => void;
	back: () => void;
	goTo: (step: number) => void;
}

const StepperContext = createContext<StepperContextValue | null>(null);

interface StepperProviderProps {
	children: React.ReactNode;
}

export const StepperProvider: React.FC<StepperProviderProps> = ({
	children
}) => {
	const { search } = useLocation();
	const query = useMemo(() => new URLSearchParams(search), [search]);
	const currentSetupType = query.get(
		'setupType'
	) as AssignmentSetupType | null;
	const assignmentId = query.get('assignmentId');
	const isEditMode = Boolean(assignmentId || query.get('mode') === 'edit');
	const isScheduledEdit = query.get('isScheduled') === 'true';

	const [current, setCurrent] = useState(0);
	const [setupType, setSetupType] = useState<AssignmentSetupType>(
		currentSetupType ?? 'Quick Setup'
	);
	const [draft, setDraft] = useState<AssignmentDraft>(() =>
		buildDraftFromAssignmentId(assignmentId)
	);
	const steps = isScheduledEdit
		? getScheduledEditSteps(setupType)
		: getStepsByType(setupType);
	const totalSteps = steps.length;

	useEffect(() => {
		setCurrent((prev) => Math.min(prev, totalSteps - 1));
	}, [totalSteps]);

	useEffect(() => {
		if (currentSetupType) {
			setSetupType(currentSetupType);
		}
		setDraft(buildDraftFromAssignmentId(assignmentId));
		setCurrent(0);
	}, [currentSetupType, assignmentId]);

	const next = () => setCurrent((prev) => Math.min(prev + 1, totalSteps - 1));
	const back = () => setCurrent((prev) => Math.max(prev - 1, 0));
	const goTo = (step: number) => {
		if (step >= 0 && step < totalSteps) setCurrent(step);
	};
	const updateDraft = (updater: (prev: AssignmentDraft) => AssignmentDraft) =>
		setDraft((prev) => updater(prev));
	const updateConfigure = (data: Partial<ConfigureState>) =>
		setDraft((prev) => ({
			...prev,
			configure: { ...prev.configure, ...data }
		}));
	const toggleQuickCourseSelection = (id: number) => {
		setDraft((prev) => {
			const selectedIds = prev.quickSetup.selectedLearningPathCourseIds;
			const nextIds = selectedIds.includes(id)
				? selectedIds.filter((item) => item !== id)
				: [...selectedIds, id];
			return {
				...prev,
				quickSetup: {
					...prev.quickSetup,
					selectedLearningPathCourseIds: nextIds
				}
			};
		});
	};
	const toggleCustomCourseSelection = (
		field: 'selectedLearningPathCourseIds' | 'selectedIndividualCourseIds',
		id: number
	) => {
		setDraft((prev) => {
			const selectedIds = prev.customSetup[field];
			const nextIds = selectedIds.includes(id)
				? selectedIds.filter((item) => item !== id)
				: [...selectedIds, id];
			return {
				...prev,
				customSetup: {
					...prev.customSetup,
					[field]: nextIds
				}
			};
		});
	};
	const setSelectedAssignUsersOptionId = (id: number) =>
		setDraft((prev) => ({
			...prev,
			customSetup: {
				...prev.customSetup,
				selectedAssignUsersOptionId: id
			}
		}));
	const toggleAssignUsersSelection = (
		field:
			| 'selectedOrganizationIds'
			| 'selectedPropertyIds'
			| 'selectedRoleIds'
			| 'selectedGroupIds'
			| 'selectedIndividualUserIds',
		id: number
	) => {
		setDraft((prev) => {
			const selectedIds = prev.customSetup[field];
			const nextIds = selectedIds.includes(id)
				? selectedIds.filter((item) => item !== id)
				: [...selectedIds, id];
			return {
				...prev,
				customSetup: {
					...prev.customSetup,
					[field]: nextIds
				}
			};
		});
	};
	const setAssignUsersSelection = (
		field:
			| 'selectedOrganizationIds'
			| 'selectedPropertyIds'
			| 'selectedRoleIds'
			| 'selectedGroupIds'
			| 'selectedIndividualUserIds',
		ids: number[]
	) => {
		setDraft((prev) => ({
			...prev,
			customSetup: {
				...prev.customSetup,
				[field]: ids
			}
		}));
	};
	const setAssignUsersPropertyFilter = (
		field:
			| 'selectedRolePropertyIds'
			| 'selectedGroupPropertyIds'
			| 'selectedIndividualPropertyIds',
		ids: number[]
	) => {
		setDraft((prev) => ({
			...prev,
			customSetup: {
				...prev.customSetup,
				[field]: ids
			}
		}));
	};

	return (
		<StepperContext.Provider
			value={{
				current,
				steps,
				totalSteps,
				isFirst: current === 0,
				isLast: current === totalSteps - 1,
				setupType,
				isEditMode,
				isScheduledEdit,
				draft,
				setSetupType,
				updateDraft,
				updateConfigure,
				toggleQuickCourseSelection,
				toggleCustomCourseSelection,
				toggleAssignUsersSelection,
				setAssignUsersSelection,
				setAssignUsersPropertyFilter,
				setSelectedAssignUsersOptionId,
				next,
				back,
				goTo
			}}
		>
			{children}
		</StepperContext.Provider>
	);
};

export const useStepper = (): StepperContextValue => {
	const context = useContext(StepperContext);
	if (!context) {
		throw new Error('useStepper must be used within a StepperProvider');
	}
	return context;
};
